import React, { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
import { ResultCodeEnum, TransactionCategory, PaymentMethod } from '@shipstation/financial-services-ui-components';

import { createPaymentSessionApi, getPaymentConfigApi, getPaymentSummaryListApi } from './api';
import { messages } from './messages';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

/**
 * @typedef {import('./types/fsui').Session} Session
 * @typedef {import('./types/fsui').AuctanePaySession} PreloadedSession
 * @typedef {import('./types/fsui').FsuiContextType} FsuiContextType
 * @typedef {import('./types/fsui').FsuiState} FsuiState
 * @typedef {import('./types/fsui').ApiConfig} ApiConfig
 */

/** @type {React.Context<FsuiContextType>} */
const FsuiContext = React.createContext({});

/**
 * Hook to access the FSUI context
 * @returns {FsuiContextType} The FSUI context
 */
export const useFsuiContext = () => useContext(FsuiContext);

const source = 'source=';
export const WALLET_REGISTRATION = 'wallet-registration';
export const WALLET_UPDATE_PAYMENT = 'wallet-update-payment';
export const ADD_PAYMENT_METHOD = 'add-payment-method';
export const SUBSCRIPTION_BILLING = 'subscription-billing';
export const SUBSCRIPTION_CHANGE_PLAN = 'subscription-change-plan';
export const SOURCE_SUBSCRIPTION_BILLING = `${source}${SUBSCRIPTION_BILLING}`;
export const SOURCE_WALLET_REGISTRATION = `${source}${WALLET_REGISTRATION}`;
export const SOURCE_WALLET_UPDATE_PAYMENT = `${source}${WALLET_UPDATE_PAYMENT}`;
export const SOURCE_ADD_PAYMENT_METHOD = `${source}${ADD_PAYMENT_METHOD}`;
export const SOURCE_SUBSCRIPTION_CHANGE_PLAN = `${source}${SUBSCRIPTION_CHANGE_PLAN}`;
const preferredPaymentTypeOrder = [PaymentMethod.PayByBankUS, PaymentMethod.PayByBankEU, PaymentMethod.CreditCard];

/** @type {FsuiState} */
const initialState = {
  error: '',
  session: null,
  orderRef: null,
  paymentDataStoreRes: null,
  preloadedSession: null,
  addtlReturnParams: '',
  shouldSkipSessionCreation: false,
};

/**
 * FSUI Context Provider component
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.currencyCode - Currency code
 * @param {TransactionCategory} props.transactionCategory - Transaction category
 * @param {PreloadedSession} [props.preloadedSession] - Initial preloaded session
 * @param {string} [props.addtlReturnParams] - Additional return parameters (string matching query=query&params=params)
 * @param {boolean} [props.shouldSkipSessionCreation] - Whether to skip session creation
 * @returns {JSX.Element} The provider component
 */
export const FsuiContextProvider = ({
  children,
  currencyCode,
  transactionCategory,
  preloadedSession: initialPreloadedSession,
  addtlReturnParams = '',
  shouldSkipSessionCreation = false,
}) => {
  const [state, setState] = useAsyncSafeState({
    ...initialState,
    preloadedSession: initialPreloadedSession || null,
    session: initialPreloadedSession
      ? {
          id: initialPreloadedSession.sessionId,
          data: initialPreloadedSession.sessionData,
        }
      : null,
  });
  const [isPreloadingSession, setIsPreloadingSession] = useAsyncSafeState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  /**
   * Creates a payment session
   * This is executed to create the preloadedSession inside of SSUI that gets passed in to FSUI providers
   * @async
   * @param {boolean} [shouldForceNewSession=false] - Whether to force a new session creation regardless of current one existing
   * @returns {Promise<Session|null>} The created session or null
   */
  const createPaymentSession = useCallback(
    async (shouldForceNewSession = false) => {
      if ((state.session && !shouldForceNewSession) || isPreloadingSession) {
        return state.session;
      }

      setIsPreloadingSession(true);

      try {
        const data = await createPaymentSessionApi({
          currencyCode,
          transactionCategory,
          addtlReturnParams,
        });

        const eligiblePaymentMethodTypes = data.eligiblePaymentMethodTypes.sort((a, b) => {
          const indexA = preferredPaymentTypeOrder.indexOf(a);
          const indexB = preferredPaymentTypeOrder.indexOf(b);
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });

        const { id, metadata } = data;
        // remove PayPal until we officially support it again
        const filteredTypes = eligiblePaymentMethodTypes.filter((type) => type !== PaymentMethod.PayPal);
        const newSession = {
          id: metadata.adyenSessionId,
          data: metadata.adyenSessionData,
        };
        setState({
          ...initialState,
          session: newSession,
          preloadedSession: {
            ...data,
            id: metadata.adyenSessionId,
            sessionId: metadata.adyenSessionId,
            sessionData: metadata.adyenSessionData,
            expirationDateTime: metadata.adyenExpiration,
            metadata,
            eligiblePaymentMethodTypes: filteredTypes,
            auctanePaySessionId: id,
          },
          auctanePaySessionId: id,
        });
        return newSession;
      } catch (error) {
        setState((prevState) => ({ ...prevState, error: error.message }));
        throw error;
      } finally {
        setIsPreloadingSession(false);
      }
    },
    [state.session, isPreloadingSession, setIsPreloadingSession, currencyCode, transactionCategory, addtlReturnParams, setState],
  );

  /**
   * Effect to create a payment session if needed
   */
  useEffect(() => {
    if (!initialPreloadedSession && !state.session && !shouldSkipSessionCreation && !isPreloadingSession && !state.error) {
      createPaymentSession();
    }
  }, [createPaymentSession, initialPreloadedSession, state.session, shouldSkipSessionCreation, isPreloadingSession, state.error]);

  /**
   * Handles payment completion
   * @param {object} result - The payment result
   * @param {() => void} onSuccess - Callback to execute on successful payment
   * @param {() => void} onErrorHandler - Callback to execute on payment error
   */
  const onPaymentCompleted = useCallback((result, onSuccess, onErrorHandler) => {
    if (result.resultCode === ResultCodeEnum.Authorised) {
      onSuccess();
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.paymentDetailsSuccessTitle} />,
        content: <FormattedMessage {...messages.paymentDetailsSuccessMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
      });
    } else {
      onErrorHandler();
    }
  }, []);

  /**
   * Handles payment errors
   * @returns {void}
   */
  const onError = useCallback(() => {
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.paymentDetailsErrorTitle} />,
      content: <FormattedMessage {...messages.paymentDetailsErrorMessage} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
    });
  }, []);

  /**
   * API configuration for payment operations
   * @type {ApiConfig}
   */
  const apiConfig = useMemo(
    () => ({
      /**
       * Gets the session ID
       * @param {object} data - The data object
       * @param {string} data.currencyCode - The currency code
       * @returns {Promise<PreloadedSession>} The preloaded session
       * @throws {Error} If currency code is missing
       */
      getSessionId: async (data) => {
        if (state.preloadedSession) {
          return state.preloadedSession;
        }
        if (!data || !data.currencyCode) {
          throw new Error('Currency code is required to create a session.');
        }

        if (state.error) {
          // prevents infinite createSession loop if error is set
          throw new Error(state.error);
        }

        const session = await createPaymentSession();
        return session;
      },
      /**
       * Gets payment methods (not used at this time)
       * @returns {Promise<void>}
       */
      // oxlint-disable-next-line no-empty-function
      getPaymentMethods: async () => {},
      /**
       * Submits payment (not used at this time)
       * @returns {Promise<void>}
       */
      // oxlint-disable-next-line no-empty-function
      submitPayment: async () => {}, // Not used at this time
      /**
       * Gets payment configuration
       * @returns {Promise<object>} The payment configuration
       */
      getConfig: getPaymentConfigApi,
      /**
       * Gets active payment methods list
       * @returns {Promise<object>} The payment configuration
       */
      listActivePaymentMethods: getPaymentSummaryListApi,
    }),
    [state.preloadedSession, createPaymentSession, state.error],
  );

  /**
   * Context value provided to consumers
   * @type {FsuiContextType}
   */
  const value = useMemo(
    () => ({
      state,
      createPaymentSession,
      onPaymentCompleted,
      onError,
      apiConfig,
      isPreloadingSession,
      preloadedSession: state.preloadedSession,
      currencyCode,
      paymentMethodId,
      setPaymentMethodId,
    }),
    [state, createPaymentSession, onPaymentCompleted, onError, apiConfig, currencyCode, isPreloadingSession, paymentMethodId],
  );

  return <FsuiContext.Provider value={value}>{children}</FsuiContext.Provider>;
};

export default FsuiContextProvider;
